import React, { useState, useCallback } from 'react';
import { Eye, EyeOff, Lock, Unlock, Plus, Trash2, ChevronDown, ChevronRight, Palette, ArrowRight } from 'lucide-react';

/**
 * Default layer colors (Rhino-inspired)
 */
const LAYER_COLORS = [
  '#ffffff', // White
  '#ff0000', // Red
  '#00ff00', // Green
  '#0000ff', // Blue
  '#ffff00', // Yellow
  '#ff00ff', // Magenta
  '#00ffff', // Cyan
  '#ff8000', // Orange
  '#8000ff', // Purple
  '#808080', // Gray
];

/**
 * LayerPanel - Rhino-style layer management
 */
export function LayerPanel({
  layers = [],
  activeLayerId = 'default',
  onLayerAdd,
  onLayerDelete,
  onLayerUpdate,
  onLayerSelect,
  counts = {},
  selectedIds = [],
  onMoveToLayer,
}) {
  const hasSelection = selectedIds.length > 0;
  const [expandedLayers, setExpandedLayers] = useState(new Set());
  const [editingLayerId, setEditingLayerId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(null);

  // Toggle layer visibility
  const toggleVisibility = useCallback((layerId) => {
    const layer = layers.find(l => l.id === layerId);
    if (layer) {
      onLayerUpdate?.(layerId, { visible: !layer.visible });
    }
  }, [layers, onLayerUpdate]);

  // Toggle layer lock
  const toggleLock = useCallback((layerId) => {
    const layer = layers.find(l => l.id === layerId);
    if (layer) {
      onLayerUpdate?.(layerId, { locked: !layer.locked });
    }
  }, [layers, onLayerUpdate]);

  // Change layer color
  const setLayerColor = useCallback((layerId, color) => {
    onLayerUpdate?.(layerId, { color });
    setShowColorPicker(null);
  }, [onLayerUpdate]);

  // Start editing layer name
  const startEditing = useCallback((layer) => {
    setEditingLayerId(layer.id);
    setEditingName(layer.name);
  }, []);

  // Save layer name
  const saveLayerName = useCallback(() => {
    if (editingLayerId && editingName.trim()) {
      onLayerUpdate?.(editingLayerId, { name: editingName.trim() });
    }
    setEditingLayerId(null);
    setEditingName('');
  }, [editingLayerId, editingName, onLayerUpdate]);

  // Add new layer
  const handleAddLayer = useCallback(() => {
    const newLayer = {
      id: `layer_${Date.now()}`,
      name: `Layer ${layers.length + 1}`,
      color: LAYER_COLORS[layers.length % LAYER_COLORS.length],
      visible: true,
      locked: false,
    };
    onLayerAdd?.(newLayer);
  }, [layers.length, onLayerAdd]);

  // Delete layer
  const handleDeleteLayer = useCallback((layerId) => {
    if (layerId === 'default') return; // Can't delete default layer
    if (layerId === activeLayerId) {
      onLayerSelect?.('default');
    }
    onLayerDelete?.(layerId);
  }, [activeLayerId, onLayerDelete, onLayerSelect]);

  // Get object count for layer
  const getLayerCount = useCallback((layerId) => {
    return counts[layerId] || 0;
  }, [counts]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-2 border-b bg-gray-50">
        <span className="text-sm font-medium text-gray-700">Layer</span>
        <button
          onClick={handleAddLayer}
          className="p-1 hover:bg-gray-200 rounded transition-colors"
          title="Neuer Layer"
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Layer List */}
      <div className="flex-1 overflow-y-auto">
        {layers.map(layer => (
          <div
            key={layer.id}
            className={`flex items-center gap-1 px-2 py-1.5 border-b border-gray-100 cursor-pointer transition-colors ${
              activeLayerId === layer.id
                ? 'bg-blue-50 border-l-2 border-l-blue-500'
                : 'hover:bg-gray-50'
            }`}
            onClick={() => onLayerSelect?.(layer.id)}
          >
            {/* Visibility Toggle */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleVisibility(layer.id);
              }}
              className={`p-1 rounded transition-colors ${
                layer.visible ? 'text-gray-600 hover:bg-gray-200' : 'text-gray-300 hover:bg-gray-200'
              }`}
              title={layer.visible ? 'Ausblenden' : 'Einblenden'}
            >
              {layer.visible ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>

            {/* Lock Toggle */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleLock(layer.id);
              }}
              className={`p-1 rounded transition-colors ${
                layer.locked ? 'text-orange-500 hover:bg-gray-200' : 'text-gray-300 hover:bg-gray-200'
              }`}
              title={layer.locked ? 'Entsperren' : 'Sperren'}
            >
              {layer.locked ? <Lock size={14} /> : <Unlock size={14} />}
            </button>

            {/* Color Indicator */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowColorPicker(showColorPicker === layer.id ? null : layer.id);
                }}
                className="w-4 h-4 rounded border border-gray-300 hover:border-gray-400 transition-colors"
                style={{ backgroundColor: layer.color }}
                title="Farbe ändern"
              />

              {/* Color Picker Dropdown */}
              {showColorPicker === layer.id && (
                <div
                  className="absolute left-0 top-6 z-50 bg-white rounded shadow-lg border p-2 grid grid-cols-5 gap-1"
                  onClick={e => e.stopPropagation()}
                >
                  {LAYER_COLORS.map(color => (
                    <button
                      key={color}
                      className={`w-5 h-5 rounded border ${
                        layer.color === color ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setLayerColor(layer.id, color)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Layer Name */}
            <div className="flex-1 min-w-0">
              {editingLayerId === layer.id ? (
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={saveLayerName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveLayerName();
                    if (e.key === 'Escape') {
                      setEditingLayerId(null);
                      setEditingName('');
                    }
                  }}
                  className="w-full px-1 py-0.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <span
                  className="text-sm truncate block"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    startEditing(layer);
                  }}
                  title={layer.name}
                >
                  {layer.name}
                </span>
              )}
            </div>

            {/* Object Count */}
            <span className="text-xs text-gray-400 mr-1">
              {getLayerCount(layer.id)}
            </span>

            {/* Move selection to this layer */}
            {hasSelection && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveToLayer?.(selectedIds, layer.id);
                }}
                className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
                title={`Auswahl auf "${layer.name}" verschieben`}
              >
                <ArrowRight size={14} />
              </button>
            )}

            {/* Delete Button */}
            {layer.id !== 'default' && layer.id !== 'construction' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteLayer(layer.id);
                }}
                className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                title="Layer löschen"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Footer with active layer info */}
      <div className="p-2 border-t bg-gray-50 text-xs text-gray-500">
        Aktiv: {layers.find(l => l.id === activeLayerId)?.name || 'Default'}
      </div>
    </div>
  );
}

export default LayerPanel;
